import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, MapPin, Clock, Users, Ticket, Tag, Image, FileText,
  Plus, Minus, Trash2, Save, ArrowLeft, ArrowRight, Check,
  AlertCircle, Settings, Eye, Globe, Lock, DollarSign, Percent,
  User, Building2, Mail, Phone, List, Type, ChevronDown, Loader2,
  Copy, ExternalLink, Layout
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import FormFieldBuilder from '../../components/admin/FormFieldBuilder';
import TemplateSelector from '../../components/admin/TemplateSelector';
import { ImageUploader } from '../../components/admin/ImageUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminEventWizard = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!eventId;
  
  // Wizard state
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  
  // Event data
  const [eventData, setEventData] = useState({
    name: '',
    slug: '',
    short_description: '',
    description: '',
    event_type: 'conference',
    category_id: null,
    organizer_type: 'michezonet',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    start_date: '',
    end_date: '',
    registration_start: '',
    registration_end: '',
    venue_name: '',
    address: '',
    city: 'Nairobi',
    country: 'Kenya',
    is_virtual: false,
    virtual_url: '',
    hero_image_url: '',
    logo_url: '',
    partner_logos_url: '',
    max_attendees: 250,
    status: 'draft',
    is_featured: false,
    is_public: true,
    tags: []
  });
  
  // Ticket types
  const [ticketTypes, setTicketTypes] = useState([]);
  
  // Registration fields
  const [registrationFields, setRegistrationFields] = useState([]);
  
  // Promo codes
  const [promoCodes, setPromoCodes] = useState([]);
  
  // Speakers
  const [speakers, setSpeakers] = useState([]);

  // Fetch categories and existing event data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const catRes = await fetch(`${API}/universal-events/categories`);
        if (catRes.ok) {
          const catData = await catRes.json();
          setCategories(catData);
        }
        
        // If editing, fetch event data
        if (isEditing) {
          const eventRes = await fetch(`${API}/universal-events/events/${eventId}`);
          if (eventRes.ok) {
            const event = await eventRes.json();
            setEventData({
              name: event.name || '',
              slug: event.slug || '',
              short_description: event.short_description || '',
              description: event.description || '',
              event_type: event.event_type || 'conference',
              category_id: event.category_id || null,
              organizer_type: event.organizer_type || 'michezonet',
              contact_person: event.contact_person || '',
              contact_email: event.contact_email || '',
              contact_phone: event.contact_phone || '',
              start_date: event.start_date ? event.start_date.slice(0, 16) : '',
              end_date: event.end_date ? event.end_date.slice(0, 16) : '',
              registration_start: event.registration_start ? event.registration_start.slice(0, 16) : '',
              registration_end: event.registration_end ? event.registration_end.slice(0, 16) : '',
              venue_name: event.venue_name || '',
              address: event.address || '',
              city: event.city || 'Nairobi',
              country: event.country || 'Kenya',
              is_virtual: event.is_virtual || false,
              virtual_url: event.virtual_url || '',
              hero_image_url: event.hero_image_url || '',
              logo_url: event.logo_url || '',
              partner_logos_url: event.partner_logos_url || '',
              max_attendees: event.max_attendees || null,
              status: event.status || 'draft',
              is_featured: event.is_featured || false,
              is_public: event.is_public !== false,
              tags: event.tags || []
            });
            setTicketTypes(event.ticket_types || []);
            setRegistrationFields(event.registration_fields || []);
            // Speakers and promo codes would be fetched separately
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [eventId, isEditing]);

  // Auto-generate slug from name
  useEffect(() => {
    if (!isEditing && eventData.name && !eventData.slug) {
      const slug = eventData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setEventData(prev => ({ ...prev, slug }));
    }
  }, [eventData.name, isEditing]);

  // Update event data
  const updateEventData = (field, value) => {
    setEventData(prev => ({ ...prev, [field]: value }));
  };

  // Add ticket type
  const addTicketType = () => {
    setTicketTypes(prev => [...prev, {
      id: `new-${Date.now()}`,
      name: '',
      description: '',
      ticket_category: 'standard',
      price: 0,
      currency: 'KES',
      price_rules: null,
      quantity_available: null,
      min_per_order: 1,
      max_per_order: 10,
      benefits: [],
      sort_order: prev.length,
      is_new: true
    }]);
  };

  // Update ticket type
  const updateTicketType = (index, field, value) => {
    setTicketTypes(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Remove ticket type
  const removeTicketType = (index) => {
    setTicketTypes(prev => prev.filter((_, i) => i !== index));
  };

  // Add registration field
  const addRegistrationField = () => {
    setRegistrationFields(prev => [...prev, {
      id: `new-${Date.now()}`,
      field_name: '',
      field_label: '',
      field_type: 'text',
      field_options: null,
      is_required: false,
      placeholder: '',
      help_text: '',
      sort_order: prev.length,
      is_new: true
    }]);
  };

  // Update registration field
  const updateRegistrationField = (index, field, value) => {
    setRegistrationFields(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Remove registration field
  const removeRegistrationField = (index) => {
    setRegistrationFields(prev => prev.filter((_, i) => i !== index));
  };

  // Add promo code
  const addPromoCode = () => {
    setPromoCodes(prev => [...prev, {
      id: `new-${Date.now()}`,
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 10,
      max_uses: null,
      valid_until: '',
      is_new: true
    }]);
  };

  // Update promo code
  const updatePromoCode = (index, field, value) => {
    setPromoCodes(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Remove promo code
  const removePromoCode = (index) => {
    setPromoCodes(prev => prev.filter((_, i) => i !== index));
  };

  // Add speaker
  const addSpeaker = () => {
    setSpeakers(prev => [...prev, {
      id: `new-${Date.now()}`,
      name: '',
      title: '',
      organization: '',
      bio: '',
      photo_url: '',
      speaker_type: 'speaker',
      is_featured: false,
      sort_order: prev.length,
      is_new: true
    }]);
  };

  // Update speaker
  const updateSpeaker = (index, field, value) => {
    setSpeakers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Remove speaker
  const removeSpeaker = (index) => {
    setSpeakers(prev => prev.filter((_, i) => i !== index));
  };

  // Save event
  const saveEvent = async (publish = false) => {
    setSaving(true);
    try {
      // Validate required fields
      if (!eventData.name || !eventData.slug || !eventData.start_date || !eventData.end_date) {
        toast.error('Please fill in all required fields');
        setSaving(false);
        return;
      }

      const payload = {
        ...eventData,
        status: publish ? 'published' : eventData.status,
        start_date: new Date(eventData.start_date).toISOString(),
        end_date: new Date(eventData.end_date).toISOString(),
        registration_start: eventData.registration_start ? new Date(eventData.registration_start).toISOString() : null,
        registration_end: eventData.registration_end ? new Date(eventData.registration_end).toISOString() : null
      };

      let eventIdResult = eventId;

      // Create or update event
      if (isEditing) {
        const response = await fetch(`${API}/universal-events/events/${eventId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          let errorMessage = 'Failed to update event';
          try {
            const errData = await response.json();
            errorMessage = errData.detail || errorMessage;
          } catch {
            const errText = await response.text();
            if (errText) errorMessage = errText;
          }
          throw new Error(errorMessage);
        }
      } else {
        const response = await fetch(`${API}/universal-events/events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          let errorMessage = 'Failed to create event';
          try {
            const errData = await response.json();
            errorMessage = errData.detail || errorMessage;
          } catch {
            const errText = await response.text();
            if (errText) errorMessage = errText;
          }
          throw new Error(errorMessage);
        }
        const data = await response.json();
        eventIdResult = data.id;
      }

      // Save ticket types
      for (const ticket of ticketTypes) {
        if (ticket.is_new) {
          await fetch(`${API}/universal-events/events/${eventIdResult}/ticket-types`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: ticket.name,
              description: ticket.description,
              ticket_category: ticket.ticket_category,
              price: parseFloat(ticket.price) || 0,
              currency: ticket.currency,
              price_rules: ticket.price_rules,
              quantity_available: ticket.quantity_available ? parseInt(ticket.quantity_available) : null,
              min_per_order: ticket.min_per_order,
              max_per_order: ticket.max_per_order,
              benefits: ticket.benefits,
              sort_order: ticket.sort_order
            })
          });
        }
      }

      // Save registration fields
      for (const field of registrationFields) {
        // Skip default fields that haven't been modified
        if (field.is_default && !field.is_new) continue;
        
        if (field.is_new) {
          await fetch(`${API}/universal-events/events/${eventIdResult}/registration-fields`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              field_name: field.field_name || field.field_label.toLowerCase().replace(/\s+/g, '_'),
              field_label: field.field_label,
              field_type: field.field_type,
              field_options: field.field_options,
              is_required: field.is_required,
              validation_rules: field.validation_rules,
              conditional_rules: field.conditional_rules,
              placeholder: field.placeholder,
              help_text: field.help_text,
              sort_order: field.sort_order
            })
          });
        }
      }

      // Save promo codes
      for (const promo of promoCodes) {
        if (promo.is_new && promo.code) {
          await fetch(`${API}/universal-events/events/${eventIdResult}/promo-codes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              code: promo.code.toUpperCase(),
              description: promo.description,
              discount_type: promo.discount_type,
              discount_value: parseFloat(promo.discount_value) || 0,
              max_uses: promo.max_uses ? parseInt(promo.max_uses) : null,
              valid_until: promo.valid_until ? new Date(promo.valid_until).toISOString() : null
            })
          });
        }
      }

      // Save speakers
      for (const speaker of speakers) {
        if (speaker.is_new && speaker.name) {
          await fetch(`${API}/universal-events/events/${eventIdResult}/speakers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: speaker.name,
              title: speaker.title,
              organization: speaker.organization,
              bio: speaker.bio,
              photo_url: speaker.photo_url,
              speaker_type: speaker.speaker_type,
              is_featured: speaker.is_featured,
              sort_order: speaker.sort_order
            })
          });
        }
      }

      toast.success(publish ? 'Event published successfully!' : 'Event saved successfully!');
      
      if (!isEditing) {
        navigate(`/admin/events/${eventIdResult}`);
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin" />
      </div>
    );
  }

  const steps = [
    { num: 1, label: 'Basic Info', icon: FileText },
    { num: 2, label: 'Date & Venue', icon: Calendar },
    { num: 3, label: 'Tickets', icon: Ticket },
    { num: 4, label: 'Registration', icon: List },
    { num: 5, label: 'Extras', icon: Settings },
    { num: 6, label: 'Review', icon: Eye }
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-event-wizard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/events" className="text-gray-500 hover:text-gray-700">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {isEditing ? 'Edit Event' : 'Create New Event'}
                </h1>
                <p className="text-sm text-gray-500">
                  {eventData.name || 'Untitled Event'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {eventData.slug && (
                <Link
                  to={`/events/${eventData.slug}`}
                  target="_blank"
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  <ExternalLink className="w-4 h-4" />
                  Preview
                </Link>
              )}
              <Button
                variant="outline"
                onClick={() => saveEvent(false)}
                disabled={saving}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Draft
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => saveEvent(true)}
                disabled={saving}
              >
                Publish Event
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {steps.map((s, idx) => (
              <React.Fragment key={s.num}>
                <button
                  onClick={() => setStep(s.num)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    step === s.num
                      ? 'bg-green-100 text-green-700'
                      : step > s.num
                      ? 'text-green-600'
                      : 'text-gray-400'
                  }`}
                  data-testid={`step-${s.num}`}
                >
                  <s.icon className="w-5 h-5" />
                  <span className="hidden sm:inline text-sm font-medium">{s.label}</span>
                </button>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${step > s.num ? 'bg-green-500' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-xl p-6 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Name *
                  </label>
                  <input
                    type="text"
                    value={eventData.name}
                    onChange={(e) => updateEventData('name', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., National Conference on Mental Wellness 2026"
                    data-testid="event-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL Slug *
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">/events/</span>
                    <input
                      type="text"
                      value={eventData.slug}
                      onChange={(e) => updateEventData('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                      className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="mental-wellness-conference-2026"
                      data-testid="event-slug-input"
                    />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Type
                    </label>
                    <select
                      value={eventData.event_type}
                      onChange={(e) => updateEventData('event_type', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      data-testid="event-type-select"
                    >
                      <option value="conference">Conference</option>
                      <option value="workshop">Workshop</option>
                      <option value="tournament">Tournament</option>
                      <option value="festival">Festival</option>
                      <option value="training">Training</option>
                      <option value="networking">Networking</option>
                      <option value="exhibition">Exhibition</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={eventData.category_id || ''}
                      onChange={(e) => updateEventData('category_id', e.target.value || null)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Short Description
                  </label>
                  <input
                    type="text"
                    value={eventData.short_description}
                    onChange={(e) => updateEventData('short_description', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="A brief tagline for the event"
                    maxLength={200}
                  />
                  <p className="text-xs text-gray-500 mt-1">{eventData.short_description.length}/200</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Description
                  </label>
                  <textarea
                    value={eventData.description}
                    onChange={(e) => updateEventData('description', e.target.value)}
                    rows={6}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Detailed description of the event (HTML supported)"
                  />
                </div>
                
                {/* Event Poster / Hero Image */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Event Poster / Hero Image
                  </label>
                  
                  {/* Current Image Preview */}
                  {eventData.hero_image_url && (
                    <div className="mb-3 relative bg-gray-100 rounded-lg">
                      <img
                        src={eventData.hero_image_url.startsWith('http') ? eventData.hero_image_url : `${process.env.REACT_APP_BACKEND_URL}${eventData.hero_image_url}`}
                        alt="Event poster preview"
                        className="w-full h-auto max-h-64 object-contain rounded-lg border border-gray-200"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => updateEventData('hero_image_url', '')}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Image Uploader */}
                  <ImageUploader
                    category="posters"
                    currentUrl={null}
                    onUpload={(result) => {
                      const url = result.url || result.path;
                      updateEventData('hero_image_url', url);
                    }}
                    label="Upload Image"
                  />
                  
                  {/* Or enter URL manually */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">Or enter image URL:</p>
                    <input
                      type="url"
                      value={eventData.hero_image_url}
                      onChange={(e) => updateEventData('hero_image_url', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                </div>
                
                {/* Event Logo */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Event Logo
                  </label>
                  
                  {/* Current Logo Preview */}
                  {eventData.logo_url && (
                    <div className="mb-3 relative inline-block">
                      <img
                        src={eventData.logo_url.startsWith('http') ? eventData.logo_url : `${process.env.REACT_APP_BACKEND_URL}${eventData.logo_url}`}
                        alt="Event logo preview"
                        className="h-20 object-contain rounded-lg border border-gray-200 bg-white p-2"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => updateEventData('logo_url', '')}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  
                  {/* Logo Uploader */}
                  <ImageUploader
                    category="logos"
                    currentUrl={null}
                    onUpload={(result) => {
                      const url = result.url || result.path;
                      updateEventData('logo_url', url);
                    }}
                    label="Upload Logo"
                  />
                  
                  {/* Or enter URL manually */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">Or enter logo URL:</p>
                    <input
                      type="url"
                      value={eventData.logo_url}
                      onChange={(e) => updateEventData('logo_url', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </div>
                
                {/* Partner Logos */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Partner / Sponsor Logos
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Add multiple partner logos. They will be displayed on the event page and tickets.
                  </p>
                  
                  {/* Current Partner Logos */}
                  {eventData.partner_logos_url && (
                    <div className="mb-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {(eventData.partner_logos_url.split(',').filter(Boolean)).map((logo, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={logo.trim().startsWith('http') ? logo.trim() : `${process.env.REACT_APP_BACKEND_URL}${logo.trim()}`}
                            alt={`Partner ${index + 1}`}
                            className="w-full h-16 object-contain rounded-lg border border-gray-200 bg-white p-1"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const logos = eventData.partner_logos_url.split(',').filter(Boolean);
                              logos.splice(index, 1);
                              updateEventData('partner_logos_url', logos.join(','));
                            }}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Partner Logos Uploader */}
                  <ImageUploader
                    category="partners"
                    multiple={true}
                    maxFiles={10}
                    onUpload={(results) => {
                      const existingLogos = eventData.partner_logos_url ? eventData.partner_logos_url.split(',').filter(Boolean) : [];
                      if (Array.isArray(results)) {
                        const newLogos = results.map(r => r.url || r.path);
                        updateEventData('partner_logos_url', [...existingLogos, ...newLogos].join(','));
                      } else if (results?.url || results?.path) {
                        const newUrl = results.url || results.path;
                        updateEventData('partner_logos_url', [...existingLogos, newUrl].join(','));
                      }
                    }}
                    label="Upload Partner Logos"
                  />
                  
                  {/* Or enter URLs manually */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">Or enter logo URLs (comma-separated):</p>
                    <textarea
                      value={eventData.partner_logos_url}
                      onChange={(e) => updateEventData('partner_logos_url', e.target.value)}
                      rows={2}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="https://example.com/partner1.png, https://example.com/partner2.png"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Date & Venue */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-xl p-6 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Date & Venue</h2>
              
              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date & Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={eventData.start_date}
                      onChange={(e) => updateEventData('start_date', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      data-testid="start-date-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date & Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={eventData.end_date}
                      onChange={(e) => updateEventData('end_date', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      data-testid="end-date-input"
                    />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Registration Opens
                    </label>
                    <input
                      type="datetime-local"
                      value={eventData.registration_start}
                      onChange={(e) => updateEventData('registration_start', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Registration Closes
                    </label>
                    <input
                      type="datetime-local"
                      value={eventData.registration_end}
                      onChange={(e) => updateEventData('registration_end', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="is_virtual"
                    checked={eventData.is_virtual}
                    onChange={(e) => updateEventData('is_virtual', e.target.checked)}
                    className="w-5 h-5 text-green-600 rounded"
                  />
                  <label htmlFor="is_virtual" className="text-sm font-medium text-gray-700">
                    This is a virtual/online event
                  </label>
                </div>
                
                {eventData.is_virtual && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Virtual Event URL
                    </label>
                    <input
                      type="url"
                      value={eventData.virtual_url}
                      onChange={(e) => updateEventData('virtual_url', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="https://zoom.us/j/..."
                    />
                  </div>
                )}
                
                {!eventData.is_virtual && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Venue Name
                      </label>
                      <input
                        type="text"
                        value={eventData.venue_name}
                        onChange={(e) => updateEventData('venue_name', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="e.g., KCA University"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Address
                      </label>
                      <input
                        type="text"
                        value={eventData.address}
                        onChange={(e) => updateEventData('address', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Full street address"
                      />
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          City
                        </label>
                        <input
                          type="text"
                          value={eventData.city}
                          onChange={(e) => updateEventData('city', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Country
                        </label>
                        <input
                          type="text"
                          value={eventData.country}
                          onChange={(e) => updateEventData('country', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Attendees
                  </label>
                  <input
                    type="number"
                    value={eventData.max_attendees || ''}
                    onChange={(e) => updateEventData('max_attendees', e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Leave empty for unlimited"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Tickets */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Ticket Types</h2>
                  <Button onClick={addTicketType} variant="outline" data-testid="add-ticket-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Ticket Type
                  </Button>
                </div>
                
                {ticketTypes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Ticket className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No ticket types yet. Add your first ticket type.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {ticketTypes.map((ticket, idx) => (
                      <div key={ticket.id} className="border border-gray-200 rounded-lg p-4" data-testid={`ticket-config-${idx}`}>
                        <div className="flex items-start justify-between mb-4">
                          <span className="text-sm font-medium text-gray-500">Ticket #{idx + 1}</span>
                          <button
                            onClick={() => removeTicketType(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">Name *</label>
                            <input
                              type="text"
                              value={ticket.name}
                              onChange={(e) => updateTicketType(idx, 'name', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                              placeholder="e.g., Early Bird, VIP"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">Price (KES) *</label>
                            <input
                              type="number"
                              value={ticket.price}
                              onChange={(e) => updateTicketType(idx, 'price', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                              placeholder="0 for free"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">Category</label>
                            <select
                              value={ticket.ticket_category}
                              onChange={(e) => updateTicketType(idx, 'ticket_category', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            >
                              <option value="standard">Standard</option>
                              <option value="vip">VIP</option>
                              <option value="early_bird">Early Bird</option>
                              <option value="student">Student</option>
                              <option value="group">Group</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">Quantity Available</label>
                            <input
                              type="number"
                              value={ticket.quantity_available || ''}
                              onChange={(e) => updateTicketType(idx, 'quantity_available', e.target.value || null)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                              placeholder="Unlimited"
                            />
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          <label className="block text-sm text-gray-600 mb-1">Description</label>
                          <textarea
                            value={ticket.description || ''}
                            onChange={(e) => updateTicketType(idx, 'description', e.target.value)}
                            rows={2}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            placeholder="What's included with this ticket"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 4: Registration Fields - Dynamic Form Builder */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* Template Selector */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <TemplateSelector
                  onSelect={(fields) => {
                    // Merge template fields with IDs
                    const fieldsWithIds = fields.map((f, idx) => ({
                      ...f,
                      id: `template-${idx}-${Date.now()}`,
                      sort_order: idx,
                      is_new: true
                    }));
                    setRegistrationFields(fieldsWithIds);
                    toast.success('Template applied! You can customize the fields below.');
                  }}
                  currentFields={registrationFields}
                />
              </div>
              
              {/* Form Field Builder */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <FormFieldBuilder
                  fields={registrationFields}
                  onChange={setRegistrationFields}
                  ticketTypes={ticketTypes}
                />
              </div>
            </motion.div>
          )}

          {/* Step 5: Extras (Speakers & Promo Codes) */}
          {step === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* Contact Info */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Contact Person</label>
                    <input
                      type="text"
                      value={eventData.contact_person}
                      onChange={(e) => updateEventData('contact_person', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Email</label>
                    <input
                      type="email"
                      value={eventData.contact_email}
                      onChange={(e) => updateEventData('contact_email', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={eventData.contact_phone}
                      onChange={(e) => updateEventData('contact_phone', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Speakers */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Speakers (Optional)</h2>
                  <Button onClick={addSpeaker} variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Speaker
                  </Button>
                </div>
                
                {speakers.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No speakers added yet.</p>
                ) : (
                  <div className="space-y-3">
                    {speakers.map((speaker, idx) => (
                      <div key={speaker.id} className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1 grid sm:grid-cols-3 gap-3">
                          <input
                            type="text"
                            value={speaker.name}
                            onChange={(e) => updateSpeaker(idx, 'name', e.target.value)}
                            className="border border-gray-300 rounded px-3 py-2 text-sm"
                            placeholder="Name"
                          />
                          <input
                            type="text"
                            value={speaker.title}
                            onChange={(e) => updateSpeaker(idx, 'title', e.target.value)}
                            className="border border-gray-300 rounded px-3 py-2 text-sm"
                            placeholder="Title"
                          />
                          <input
                            type="text"
                            value={speaker.organization}
                            onChange={(e) => updateSpeaker(idx, 'organization', e.target.value)}
                            className="border border-gray-300 rounded px-3 py-2 text-sm"
                            placeholder="Organization"
                          />
                        </div>
                        <button onClick={() => removeSpeaker(idx)} className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Promo Codes */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Promo Codes (Optional)</h2>
                  <Button onClick={addPromoCode} variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Code
                  </Button>
                </div>
                
                {promoCodes.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No promo codes yet.</p>
                ) : (
                  <div className="space-y-3">
                    {promoCodes.map((promo, idx) => (
                      <div key={promo.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1 grid sm:grid-cols-4 gap-3">
                          <input
                            type="text"
                            value={promo.code}
                            onChange={(e) => updatePromoCode(idx, 'code', e.target.value.toUpperCase())}
                            className="border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                            placeholder="CODE"
                          />
                          <select
                            value={promo.discount_type}
                            onChange={(e) => updatePromoCode(idx, 'discount_type', e.target.value)}
                            className="border border-gray-300 rounded px-3 py-2 text-sm"
                          >
                            <option value="percentage">Percentage</option>
                            <option value="fixed">Fixed Amount</option>
                          </select>
                          <input
                            type="number"
                            value={promo.discount_value}
                            onChange={(e) => updatePromoCode(idx, 'discount_value', e.target.value)}
                            className="border border-gray-300 rounded px-3 py-2 text-sm"
                            placeholder={promo.discount_type === 'percentage' ? '10' : '500'}
                          />
                          <input
                            type="number"
                            value={promo.max_uses || ''}
                            onChange={(e) => updatePromoCode(idx, 'max_uses', e.target.value)}
                            className="border border-gray-300 rounded px-3 py-2 text-sm"
                            placeholder="Max uses"
                          />
                        </div>
                        <button onClick={() => removePromoCode(idx)} className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 6: Review */}
          {step === 6 && (
            <motion.div
              key="step6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-xl p-6 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Review & Publish</h2>
              
              <div className="space-y-6">
                {/* Event Summary */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-3">Event Details</h3>
                  <dl className="grid sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-gray-500">Name</dt>
                      <dd className="font-medium text-gray-900">{eventData.name || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Type</dt>
                      <dd className="font-medium text-gray-900 capitalize">{eventData.event_type}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Date</dt>
                      <dd className="font-medium text-gray-900">
                        {eventData.start_date ? new Date(eventData.start_date).toLocaleDateString() : '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Location</dt>
                      <dd className="font-medium text-gray-900">
                        {eventData.is_virtual ? 'Virtual Event' : `${eventData.venue_name || '-'}, ${eventData.city}`}
                      </dd>
                    </div>
                  </dl>
                </div>
                
                {/* Tickets Summary */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-3">Ticket Types ({ticketTypes.length})</h3>
                  {ticketTypes.length > 0 ? (
                    <ul className="space-y-2 text-sm">
                      {ticketTypes.map((t, i) => (
                        <li key={i} className="flex justify-between text-gray-900">
                          <span>{t.name || `Ticket #${i + 1}`}</span>
                          <span className="font-medium">KES {t.price?.toLocaleString() || 0}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">No tickets configured</p>
                  )}
                </div>
                
                {/* Registration Fields Summary */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-3">Registration Fields ({registrationFields.length})</h3>
                  {registrationFields.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {registrationFields.map((f, i) => (
                        <span key={i} className="px-2 py-1 bg-white rounded text-sm text-gray-700 border border-gray-200">
                          {f.field_label || `Field #${i + 1}`}
                          {f.is_required && <span className="text-red-500 ml-1">*</span>}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Using default fields only</p>
                  )}
                </div>

                {/* Visibility Settings */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-3">Visibility</h3>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={eventData.is_public}
                        onChange={(e) => updateEventData('is_public', e.target.checked)}
                        className="w-4 h-4 text-green-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Public</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={eventData.is_featured}
                        onChange={(e) => updateEventData('is_featured', e.target.checked)}
                        className="w-4 h-4 text-green-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Featured</span>
                    </label>
                  </div>
                </div>
                
                {/* Warnings */}
                {ticketTypes.length === 0 && (
                  <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-800">No tickets configured</p>
                      <p className="text-sm text-yellow-700">Add at least one ticket type before publishing.</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            data-testid="prev-step-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {step < 6 ? (
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => setStep(Math.min(6, step + 1))}
              data-testid="next-step-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => saveEvent(true)}
              disabled={saving || ticketTypes.length === 0}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Publish Event
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEventWizard;
